import type { IsoDate, Workout } from "./types.js";
import { weekdayOf } from "./dates.js";
import { PLAN_END } from "./plan.js";

/**
 * 台北馬賽季的課表：跑步為主、重訓為輔，只求完賽。
 *
 * 由日期推導，不存在資料庫裡——同一天永遠得到同一份課表。**不依執行狀況調整**：
 * 漏跑不補，也不疊到下週，因為把距離疊上去正是最容易受傷的做法。
 *
 * 回傳的第三個值是 CSS class，同時被月曆樣式與能量估算（energyDayConfig）
 * 使用，改動時兩邊都會受影響。第二季刻意不新增 class：`main.css` 靠精確選取，
 * 多一種會靜默沒有樣式。
 *
 * 長跑起點是 6–9 km：最近四週最長跑過 6–9 km，而且目前沒有傷痛。
 */

/** 週二輕鬆跑的分鐘數。賽前一週降回來。 */
const EASY_RUN_MINUTES: Record<IsoDate, number> = {
  "2026-11-10": 30,
  "2026-11-17": 35,
  "2026-11-24": 40,
  "2026-12-01": 45,
  "2026-12-08": 40,
  "2026-12-15": 25
};

/** 週六長跑。11/28、12/5 是高峰，12/12 起減量。 */
const LONG_RUN_KM: Record<IsoDate, number> = {
  "2026-11-07": 8,
  "2026-11-14": 9,
  "2026-11-21": 8,
  "2026-11-28": 11,
  "2026-12-05": 13,
  "2026-12-12": 9
};

/** 週一短重訓從這天起停止，讓腿在比賽前恢復。 */
const SHORT_STRENGTH_ENDS: IsoDate = "2026-12-07";

const WALK: Workout = ["休息／步行", "8,000–10,000步＋10分鐘活動度", "rest"];

/** 週四全身重訓，腿部的量逐週遞減。 */
const FULL_BODY = "深蹲或腿推、臀推、划船、胸推、核心";

const SPECIAL_DAYS: Record<IsoDate, Workout> = {
  "2026-12-03": ["全身重訓", `${FULL_BODY}；腿部2組`, "legs"],
  "2026-12-10": ["全身重訓", `${FULL_BODY}；腿部2組，重量減輕，不做到力竭`, "legs"],
  "2026-12-17": ["活動度", "不重訓，20分鐘活動度", "rest"],
  "2026-12-18": ["賽前輕鬆跑", "20分鐘，不加速", "run"],
  "2026-12-19": ["賽前放鬆", "休息或3 km慢跑；早點睡", "recovery"],
  [PLAN_END]: ["臺北馬拉松半程", "賽前晨量體重；不追配速，可跑走交替，完賽就好", "longrun"]
};

/** 半馬的距離，四捨五入到畫面上寫得下的一位。 */
const RACE_KM = 21.1;

/** 那一天安排的長跑公里數。不是長跑日是 0。 */
export function longRunKm(date: IsoDate): number {
  return date === PLAN_END ? RACE_KM : LONG_RUN_KM[date] ?? 0;
}

export function workoutFor(date: IsoDate): Workout {
  const special = SPECIAL_DAYS[date];
  if (special) return special;

  const weekday = weekdayOf(date);

  if (weekday === 1) {
    return date < SHORT_STRENGTH_ENDS
      ? ["上半身＋核心", "短重訓30分鐘：胸推、划船、肩推、核心；不練腿", "push"]
      : WALK;
  }
  if (weekday === 2) {
    const minutes = EASY_RUN_MINUTES[date];
    return ["輕鬆跑", `${minutes ?? 30}分鐘；可完整說話，必要時跑4分走1分`, "run"];
  }
  if (weekday === 3) return WALK;
  if (weekday === 4) return ["全身重訓", `${FULL_BODY}；腿部3組`, "legs"];
  if (weekday === 5) return ["輕鬆跑＋短加速", "30分鐘輕鬆跑，加4趟短加速；加速不是衝刺", "run"];
  if (weekday === 6) {
    const km = LONG_RUN_KM[date];
    return ["長距離輕鬆跑", `${km ?? 8} km；不追配速，可跑4分走1分`, "longrun"];
  }
  return ["恢復", "休息；精神良好可快走20–30分", "recovery"];
}
